import React, { useMemo } from "react";
import { Form, Row, Col, Button, ListGroup } from "react-bootstrap";
import CreatableSelect from "react-select/creatable";
import { Link } from "react-router-dom";
import { GenericTable } from "components/GenericTable";
import { createElementIdFactory, createReusableTableElementIds } from "utils/elementIds";
import { createColumnHelper } from "@tanstack/react-table";
import "containers/common/List.css";
import "containers/common/Table.css";
import { PAGE_SIZES } from "utils/pageSizeConfig";
function PropertyList({
  data,
  isLoading,
  isAuthenticated,
  controlType,
  propData,
  updateTableState,
  saveBrowseList,
  browseListsOptions,
  handleBrowseListChange,
  handleBrowseListInputChange,
  handleBrowseListCreate,
  browseListInputValue,
  deleteBrowseList,
  selectedBrowseListOption,
  searchBrowseList,
  handleBrowseListBlur
}) {
  const dataSet = "properties";
  const tableData = Array.isArray(data) ? data : [];
  const sourceRows = Array.isArray(propData) && propData.length > 0 ? propData : tableData;
  const columnHelper = useMemo(() => createColumnHelper(), []);
  const createId = useMemo(() => createElementIdFactory("property-list"), []);

  function updateSelectionByIndex(rowIndex, selected) {
    if (!updateTableState) return;
    const updatedRows = sourceRows.map((row, index) =>
      index === rowIndex ? { ...row, selected } : row
    );
    updateTableState(updatedRows, dataSet);
  }

  function setSelectionForAll(selected) {
    if (!updateTableState) return;
    const updatedRows = sourceRows.map((row) => ({ ...row, selected }));
    updateTableState(updatedRows, dataSet);
  }

  function selectAll() {
    setSelectionForAll(true);
  }

  function deselectAll() {
    setSelectionForAll(false);
  }

  const col_selected = columnHelper.accessor("selected", {
    id: "selected",
    header: "",
    size: 50,
    meta: { align: "center" },
    cell: (info) => (
      <Form.Check
        id={createId("table", "selected", "checkbox", info.row.index)}
        checked={Boolean(info.getValue())}
        onChange={(event) => updateSelectionByIndex(info.row.index, event.target.checked)}
        type="checkbox"
      />
    ),
  });
  const col_property_key = columnHelper.accessor("property_key", {
    id: "property_key",
    header: "Cityscope Reference",
    size: 80,
    cell: (info) => {
      const selectedPropertyKeys = (tableData || [])
        .filter((prop) => prop.selected)
        .map((prop) => prop.property_key);
      const propertyKeys = (tableData || [])
        .map((prop) => prop.property_key)
        .filter(Boolean);
      const hasSelectedProperties = selectedPropertyKeys.length > 0;

      return (
        <Link
          id={createId("table", "property-link", info.row.index)}
          to={{
            pathname: `/properties/${info.getValue()}`,
          }}
          state={{
            data: hasSelectedProperties ? selectedPropertyKeys : propertyKeys,
            showIndexNavigation: propertyKeys.length > 0,
          }}
        >
          {info.getValue()}
        </Link>
      );
    },
  });
  const col_building_name = columnHelper.accessor("building_name", {
    id: "building_name",
    header: "Building Name",
    cell: (info) => info.getValue(),
    meta: { wrap: true },
  });

  const col_main_address = columnHelper.accessor("main_address", {
    id: "main_address",
    header: () => (
      <span>
        Main Address
      </span>
    ),
    cell: (info) => info.getValue(),
    size: 70,
    meta: { wrap: true },
  });
  const col_street_num_low = columnHelper.accessor("street_num_low", {
    id: "street_num_low",
    header: "St No. Low",
    cell: (info) => info.getValue(),
    size: 100,
  });

  const col_street_num_low_suffix = columnHelper.accessor("street_num_low_suffix", {
    id: "street_num_low_suffix",
    header: () => (
      <span>
        St No. Low Suffix
      </span>
    ),
    cell: (info) => info.getValue(),
    size: 70,
  });

  const col_street_num_high = columnHelper.accessor("street_num_high", {
    id: "street_num_high",
    header: "St No. High",
    cell: (info) => info.getValue(),
    size: 100,
  });

  const col_street_num_high_suffix = columnHelper.accessor("street_num_high_suffix", {
    id: "street_num_high_suffix",
    header: () => (
      <span>
        St No. High Suffix
      </span>
    ),
    cell: (info) => info.getValue(),
    size: 70,
  });

  const col_street_name = columnHelper.accessor("street_name", {
    id: "street_name",
    header: "St Name",
    cell: (info) => info.getValue(),
    meta: { wrap: true },
  });

  const col_suburb = columnHelper.accessor("suburb", {
    id: "suburb",
    header: "Suburb",
    cell: (info) => info.getValue(),
    meta: { wrap: true },
  });

  const col_state = columnHelper.accessor("state", {
    id: "state",
    header: "State",
    cell: (info) => info.getValue(),
    size: 50,
  });

  const col_postcode = columnHelper.accessor("postcode", {
    id: "postcode",
    header: "Postcode",
    cell: (info) => info.getValue(),
    size: 85,
  });

  const col_title_details = columnHelper.accessor("title_details", {
    id: "title_details",
    header: "Title",
    cell: (info) => info.getValue(),
    meta: { wrap: true },
  });

  const columns = [
    col_selected,
    col_property_key,
    col_building_name,
    col_main_address,
    col_street_num_low,
    col_street_num_low_suffix,
    col_street_num_high,
    col_street_num_high_suffix,
    col_street_name,
    col_suburb,
    col_state,
    col_postcode,
    col_title_details,
  ];

  const columns1 = [
    col_property_key,
    col_building_name,
    col_main_address,
    col_street_num_low,
    col_street_num_low_suffix,
    col_street_num_high,
    col_street_num_high_suffix,
    col_street_name,
    col_suburb,
    col_state,
    col_postcode,
  ];

  const activeColumns = controlType === "table" ? columns : columns1;
  const tableElementIds = useMemo(
    () => createReusableTableElementIds(createId, controlType === "table" ? "table" : "table2"),
    [createId, controlType]
  );

  function renderList(listData) {
    if (!listData) {
      return null;
    }
    return [{}].concat(listData).map((d, i) =>
      i !== 0 ? (
        <ListGroup.Item key={i} as="a" header={d.property_key}>
          {d.property_key + " " + d.street_name}
        </ListGroup.Item>
      ) : (
        ""
      )
    );
  }

  function renderComponent() {
    return (
      <div id={createId("root")} className="properties">
        {controlType === "table" && (
          <div>
            <Row>
              <Col className="text-left">
                <div className="d-flex gap-2 flex-wrap align-items-center">
                  <Button
                    id={createId("button", "select-all")}
                    variant="primary"
                    type="button"
                    size="sm"
                    onClick={selectAll}
                  >
                    Select All
                  </Button>
                  <Button
                    id={createId("button", "deselect-all")}
                    variant="primary"
                    type="button"
                    size="sm"
                    onClick={deselectAll}
                  >
                    Deselect All
                  </Button>
                  {browseListsOptions && (
                    <div style={{ minWidth: 240 }}>
                      <CreatableSelect
                        inputId={createId("select", "browse-list")}
                        value={selectedBrowseListOption}
                        inputValue={browseListInputValue}
                        onChange={handleBrowseListChange}
                        onInputChange={handleBrowseListInputChange}
                        options={browseListsOptions}
                        onCreateOption={handleBrowseListCreate}
                        onBlur={handleBrowseListBlur}
                        placeholder="Select a Browse List"
                        isClearable
                      />
                    </div>
                  )}
                  <Button
                    id={createId("button", "retrieve-browse-list")}
                    variant="primary"
                    type="button"
                    size="sm"
                    onClick={searchBrowseList}
                  >
                    Retrieve Browse List
                  </Button>
                  <Button
                    id={createId("button", "add-to-browse-list")}
                    variant="primary"
                    type="button"
                    size="sm"
                    onClick={saveBrowseList}
                  >
                    Add to Browse List
                  </Button>
                  {selectedBrowseListOption && (
                    <Button
                      id={createId("button", "delete-browse-list")}
                      variant="danger"
                      type="button"
                      size="sm"
                      onClick={deleteBrowseList}
                    >
                      Delete Browse List
                    </Button>
                  )}
                </div>
              </Col>
              <Col className="text-right" sm={4} md={3} lg={2}>
                <Link id={createId("link", "add-property")} to={`/properties/new`}>
                  <Button id={createId("button", "add-property")} variant="primary" type="button" size="sm">
                    Add New Property
                  </Button>
                </Link>
              </Col>
            </Row>
            <Row id={createId("records", "row")} className="mb-2">
              <Col id={createId("records", "col")}>
                <b id={createId("records", "text")}>
                  {tableData.length} of {tableData[0]?.result_count || "0"} records returned.
                </b>
              </Col>
            </Row>
          </div>
        )}

        <Row className="show-Container">
          <Col sm={12} md={12} lg={12}>
            {isLoading && (
              <div id={createId("loading-container")} className="outer-div">
                <div className="sweet-loading inner-div">
                  <span id={createId("loading-indicator")}>Loading...</span>
                </div>
              </div>
            )}
            {!isLoading && (controlType === "table" || controlType === "table2") && (
              <GenericTable
                columns={activeColumns}
                data={tableData}
                section={createId(controlType === "table" ? "table" : "table2")}
                pageSize={PAGE_SIZES.propertySearch}
                enablePagination
                hasHeaderSorting
                elementIds={tableElementIds}
                columnResizeMode="onChange"
              />
            )}
            {!isLoading && controlType !== "table" && controlType !== "table2" && renderList(tableData)}
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div id={createId("container")} className="PropertyList">
      {isAuthenticated ? renderComponent() : null}
    </div>
  );
}

export default PropertyList;
